import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    Font,
} from "@react-pdf/renderer";

// ---------------------------------------------------------------
// Estilos del documento PDF
// ---------------------------------------------------------------
const styles = StyleSheet.create({
    page: {
        fontFamily: "Helvetica",
        fontSize: 8,
        paddingTop: 28,
        paddingBottom: 28,
        paddingHorizontal: 28,
        backgroundColor: "#FFFFFF",
    },
    // ---- Header ----
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        borderBottomWidth: 2,
        borderBottomColor: "#2350C7",
        paddingBottom: 8,
        marginBottom: 14,
    },
    logo: {
        width: 44,
        height: 44,
        marginRight: 12,
    },
    headerTextBlock: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 15,
        fontFamily: "Helvetica-Bold",
        color: "#2350C7",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },
    headerSubtitle: {
        fontSize: 8,
        color: "#555555",
        marginTop: 2,
    },
    generatedAt: {
        fontSize: 7,
        color: "#888888",
        marginTop: 2,
    },
    // ---- Filtros ----
    filtersBox: {
        backgroundColor: "#f8f9fa",
        borderRadius: 4,
        padding: 8,
        marginBottom: 12,
        flexDirection: "row",
        flexWrap: "wrap",
    },
    filterItem: {
        flexDirection: "row",
        marginRight: 18,
        marginBottom: 3,
    },
    filterLabel: {
        fontFamily: "Helvetica-Bold",
        color: "#2350C7",
        fontSize: 7,
        marginRight: 3,
    },
    filterValue: {
        color: "#333333",
        fontSize: 7,
    },
    totalBox: {
        flexDirection: "row",
        marginTop: 2,
    },
    totalLabel: {
        fontFamily: "Helvetica-Bold",
        color: "#2350C7",
        fontSize: 8,
        marginRight: 4,
    },
    totalValue: {
        fontFamily: "Helvetica-Bold",
        color: "#1a1a1a",
        fontSize: 8,
    },
    // ---- Tabla ----
    tableHeaderRow: {
        flexDirection: "row",
        backgroundColor: "#2350C7",
        borderRadius: 2,
        paddingVertical: 5,
        paddingHorizontal: 2,
        marginBottom: 0,
    },
    tableHeaderCell: {
        color: "#FFFFFF",
        fontFamily: "Helvetica-Bold",
        fontSize: 7,
        textTransform: "uppercase",
        textAlign: "center",
        paddingHorizontal: 3,
    },
    tableRow: {
        flexDirection: "row",
        paddingVertical: 4,
        paddingHorizontal: 2,
        borderBottomWidth: 0.5,
        borderBottomColor: "#e0e0e0",
    },
    tableRowEven: {
        backgroundColor: "#f2f5ff",
    },
    tableRowOdd: {
        backgroundColor: "#FFFFFF",
    },
    tableCell: {
        fontSize: 7,
        color: "#333333",
        paddingHorizontal: 3,
        flexWrap: "wrap",
    },
    tableCellCenter: {
        fontSize: 7,
        color: "#333333",
        paddingHorizontal: 3,
        textAlign: "center",
        flexWrap: "wrap",
    },
    // Badge de estado
    badgeActive: {
        backgroundColor: "#d4edda",
        color: "#155724",
        borderRadius: 3,
        paddingHorizontal: 4,
        paddingVertical: 1,
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
        textTransform: "uppercase",
    },
    badgeInactive: {
        backgroundColor: "#f8d7da",
        color: "#721c24",
        borderRadius: 3,
        paddingHorizontal: 4,
        paddingVertical: 1,
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
        textTransform: "uppercase",
    },
    zeroHoursCell: {
        backgroundColor: "#C70F0F",
        color: "#FFFFFF",
        borderRadius: 2,
        paddingHorizontal: 3,
        paddingVertical: 1,
        fontSize: 7,
        textAlign: "center",
    },
    // ---- Footer ----
    footer: {
        position: "absolute",
        bottom: 16,
        left: 28,
        right: 28,
        flexDirection: "row",
        justifyContent: "space-between",
        borderTopWidth: 0.5,
        borderTopColor: "#eeeeee",
        paddingTop: 4,
    },
    footerText: {
        fontSize: 6,
        color: "#999999",
    },
    // ---- Empty ----
    emptyText: {
        textAlign: "center",
        color: "#888888",
        marginTop: 24,
        fontSize: 9,
    },
});

// ---------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------
const minutesToHours = (minutes) => {
    const m = parseFloat(minutes) || 0;
    const h = Math.floor(m / 60);
    const min = Math.round(m % 60);
    return `${h}hrs ${min}min`;
};

const now = () => {
    const d = new Date();
    return d.toLocaleDateString("es-GT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Anchos de columna (landscape letter ≈ 756pt de contenido)
const COL_WIDTHS = {
    dpi: "14%",
    name: "24%",
    station: "15%",
    job: "15%",
    type: "9%",
    status: "9%",
    hours: "14%",
};

// ---------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------
const HumanResourcesPDF = ({
    results = [],
    grandTotal = 0,
    startDate = null,
    endDate = null,
    statusUser = "",
    stationText = "Todas",
    specificUser = "",
    logoBase64 = null,
}) => {
    return (
        <Document
            title="Reporte de Recursos Humanos"
            author="ASONBOMD"
            subject="Reporte RRHH"
        >
            <Page
                size="LETTER"
                orientation="landscape"
                style={styles.page}
            >
                {/* ---- HEADER ---- */}
                <View style={styles.headerRow} fixed>
                    {logoBase64 && (
                        <Image
                            style={styles.logo}
                            src={`data:image/png;base64,${logoBase64}`}
                        />
                    )}
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerTitle}>
                            Reporte de Recursos Humanos
                        </Text>
                        <Text style={styles.headerSubtitle}>
                            Asociación Nacional de Bomberos Municipales Departamentales
                        </Text>
                        <Text style={styles.generatedAt}>
                            Generado el {now()}
                        </Text>
                    </View>
                </View>

                {/* ---- FILTROS ---- */}
                <View style={styles.filtersBox} fixed>
                    <View style={styles.filterItem}>
                        <Text style={styles.filterLabel}>Fecha Inicio:</Text>
                        <Text style={styles.filterValue}>{startDate || "—"}</Text>
                    </View>
                    <View style={styles.filterItem}>
                        <Text style={styles.filterLabel}>Fecha Fin:</Text>
                        <Text style={styles.filterValue}>{endDate || "—"}</Text>
                    </View>
                    <View style={styles.filterItem}>
                        <Text style={styles.filterLabel}>Estado:</Text>
                        <Text style={styles.filterValue}>{statusUser || "Todos"}</Text>
                    </View>
                    <View style={styles.filterItem}>
                        <Text style={styles.filterLabel}>Estación:</Text>
                        <Text style={styles.filterValue}>{stationText}</Text>
                    </View>
                    {specificUser ? (
                        <View style={styles.filterItem}>
                            <Text style={styles.filterLabel}>Usuario:</Text>
                            <Text style={styles.filterValue}>{specificUser}</Text>
                        </View>
                    ) : null}
                    <View style={styles.totalBox}>
                        <Text style={styles.totalLabel}>Total Horas:</Text>
                        <Text style={styles.totalValue}>{minutesToHours(grandTotal)}</Text>
                    </View>
                </View>

                {/* ---- TABLA: CABECERA ---- */}
                <View style={styles.tableHeaderRow} fixed>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.dpi }]}>DPI</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.name }]}>Nombre Completo</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.station }]}>Estación</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.job }]}>Puesto</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.type }]}>Tipo</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.status }]}>Estado</Text>
                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.hours }]}>Horas Totales</Text>
                </View>

                {/* ---- TABLA: FILAS ---- */}
                {results.length === 0 ? (
                    <Text style={styles.emptyText}>No se encontraron registros.</Text>
                ) : (
                    results.map((user, index) => {
                        const isEven = index % 2 === 0;
                        const hours = parseFloat(user.total_hours_report) || 0;
                        const isZero = hours === 0;

                        return (
                            <View
                                key={user.id || index}
                                style={[
                                    styles.tableRow,
                                    isEven ? styles.tableRowEven : styles.tableRowOdd,
                                ]}
                                wrap={false}
                            >
                                <Text style={[styles.tableCellCenter, { width: COL_WIDTHS.dpi }]}>
                                    {user.no_dpi || "—"}
                                </Text>
                                <Text style={[styles.tableCell, { width: COL_WIDTHS.name }]}>
                                    {user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim()}
                                </Text>
                                <Text style={[styles.tableCell, { width: COL_WIDTHS.station }]}>
                                    {user.station_text || "—"}
                                </Text>
                                <Text style={[styles.tableCell, { width: COL_WIDTHS.job }]}>
                                    {user.job_management || "—"}
                                </Text>
                                <Text style={[styles.tableCellCenter, { width: COL_WIDTHS.type }]}>
                                    {user.firefighter_type || "—"}
                                </Text>
                                <View style={[{ width: COL_WIDTHS.status, alignItems: "center", justifyContent: "center" }]}>
                                    <Text
                                        style={
                                            user.status === "Activo"
                                                ? styles.badgeActive
                                                : styles.badgeInactive
                                        }
                                    >
                                        {user.status || "—"}
                                    </Text>
                                </View>
                                <View style={[{ width: COL_WIDTHS.hours, alignItems: "center", justifyContent: "center" }]}>
                                    {isZero ? (
                                        <Text style={styles.zeroHoursCell}>
                                            {minutesToHours(hours)}
                                        </Text>
                                    ) : (
                                        <Text style={styles.tableCellCenter}>
                                            {minutesToHours(hours)}
                                        </Text>
                                    )}
                                </View>
                            </View>
                        );
                    })
                )}

                {/* ---- FOOTER con número de página ---- */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Reporte de Recursos Humanos — ASONBOMD
                    </Text>
                    <Text
                        style={styles.footerText}
                        render={({ pageNumber, totalPages }) =>
                            `Página ${pageNumber} de ${totalPages}`
                        }
                    />
                </View>
            </Page>
        </Document>
    );
};

export default HumanResourcesPDF;
